import { Editor } from '@tiptap/core';
import Link from '@tiptap/extension-link';
import ListKeymap from '@tiptap/extension-list-keymap';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { BubbleMenu, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import clsx from 'clsx';
import { EditorFormatter } from 'copilot-design-system';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import React, { useEffect } from 'react';

import { SlashCommand } from 'src/components/tiptap/custom-extensions/SlashCommand';
import { SlashMenuRenderer } from 'src/components/tiptap/custom-extensions/SlashMenuRenderer';
import { EditorMenu } from 'src/components/tiptap/editor-menu/EditorMenu';
import { executeEditorAction } from 'src/components/tiptap/editor-menu/utils';
import { RichTextStyles } from 'src/components/tiptap/RichTextStyles';
import { SlashMenu } from 'src/components/tiptap/slash-menu/SlashMenu';
import { getSlashMenuSuggestionItems } from 'src/features/notes/NoteModal/utils';

interface Props {
  description: string;
  onContentChange: (editor: Editor) => void;
  onUpdateTipTapEditor?: (edited: Editor | null) => void;
  className?: string;
}

export const BillingRichTextEditor: React.FC<Props> = ({
  description,
  onContentChange,
  onUpdateTipTapEditor,
  className,
}) => {
  const EnableNewEditorFormatter = useFeatureFlagEnabled(
    'new_editor_formatter',
  );
  const tiptapEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3],
        },
      }),
      Placeholder.configure({
        emptyEditorClass:
          'before:content-[attr(data-placeholder)] before:float-left before:text-[#9B9FA3] before:h-0 before:pointer-events-none',
        placeholder: '',
      }),
      Underline,
      Link,
      ListKeymap,
      SlashCommand.configure({
        suggestion: {
          items: getSlashMenuSuggestionItems,
          render: () =>
            SlashMenuRenderer({
              Component: SlashMenu,
              tippyProps: {
                placement: 'bottom-start',
              },
            }),
        },
      }),
    ],
    editorProps: {
      attributes: {
        class: clsx(
          className,
          'flex flex-col gap-1 mt-1 min-h-[64px] relative',
        ),
      },
    },
    content: description,
    onCreate: ({ editor }) => onContentChange(editor),
    onUpdate: ({ editor }) => onContentChange(editor),
  });

  useEffect(() => {
    onUpdateTipTapEditor?.(tiptapEditor);
  }, [onUpdateTipTapEditor, tiptapEditor]);

  const getIsActive = React.useCallback(
    (actionId: string) => {
      return tiptapEditor?.isActive(actionId) ?? false;
    },
    [tiptapEditor],
  );

  const handleAction = React.useCallback(
    (_featureType: string, actionId: string) => {
      if (!tiptapEditor) return;
      executeEditorAction(tiptapEditor, actionId);
    },
    [tiptapEditor],
  );

  return (
    <div
      className={clsx(
        'relative box-border border border-solid rounded max-h-64 overflow-auto [&>div>div]:!z-modal',
        {
          'border-black-heading': tiptapEditor?.isFocused,
          'border-non-hover-border': !tiptapEditor?.isFocused,
        },
      )}
    >
      {tiptapEditor && !EnableNewEditorFormatter && (
        <>
          <BubbleMenu
            editor={tiptapEditor}
            tippyOptions={{ duration: 100, maxWidth: 'none' }}
          >
            <EditorMenu
              editor={tiptapEditor}
              textMenuActions={['heading2', 'heading3', 'text']}
            />
          </BubbleMenu>
        </>
      )}
      {tiptapEditor && EnableNewEditorFormatter && (
        <EditorFormatter
          className="w-[-webkit-fill-available]"
          variant="sticky"
          features={['textStyle']}
          onAction={handleAction}
          getIsActiveAction={getIsActive}
        />
      )}
      <div className="px-3.5 py-3">
        <RichTextStyles>
          <EditorContent editor={tiptapEditor} />
        </RichTextStyles>
      </div>
    </div>
  );
};
